import { createModel } from "@rematch/core";
import { RootModel } from "./index";
import {
  apiErrorMessage,
  demoSession,
  DemoMeetings,
  DemoPrepareRefusal,
  DemoPrepareStatus,
  DemoPrepareStep,
  DemoScenario,
} from "../API";
import { untilProcessed } from "../demo/processing";

/**
 * Prepare demo (Dooglie staff only): the one demo a staff member can have in
 * preparation or ready, mirrored from `/demo_session`.
 *
 * The server is the source of truth for where the preparation is; every
 * action here adopts what `prepare_status` says rather than predicting it.
 * Whether the button exists at all comes from `/get_user_info`
 * (`user.demo_mode_available`, staff only), which also triggers `fetchStatus`
 * on start so the sidebar can say a demo is being prepared or is ready.
 */
type DemoState = {
  // The last `prepare_status` answer; null until one has landed.
  status: DemoPrepareStatus | null;
  // A prepare, confirm or teardown is in flight.
  busy: boolean;
  // The message from the last refused action, for the dialog to show.
  error: string | null;
  // The refusal's `error` code when the server gave one, so the dialog can
  // offer a way out (the Settings link for `salesperson_has_no_name`).
  errorCode: string | null;
};

export type PrepareDemoBody = {
  meeting_id: string;
  prospect_email: string;
  scenario: DemoScenario;
  // The company website to build from; omitted when blank.
  site?: string;
};

/** The steps a preparation passes through, in order; the dialog's timeline. */
export const PREPARE_STEPS = ["crawling", "building_tenant", "warming"] as const;

/**
 * The timeline step a status shows as. `requested` is the wait for the first
 * worker, which is about to read the site. `creating_org` and `seeding` were
 * steps of their own until the org is built alongside the workspace; a slot
 * prepared before that still reports them, and they read as the build.
 */
export const timelineStep = (status: DemoPrepareStep): (typeof PREPARE_STEPS)[number] | null => {
  switch (status) {
    case "requested":
      return "crawling";
    case "creating_org":
    case "seeding":
      return "building_tenant";
    case "crawling":
    case "building_tenant":
    case "warming":
      return status;
    default:
      return null;
  }
};

/** Still being worked on by the server: the dialog keeps polling. */
export const isPreparing = (status: DemoPrepareStatus | null): boolean =>
  status != null &&
  status.status !== "none" &&
  status.status !== "ready" &&
  status.status !== "failed";

export type ProspectTick = {
  key: "link_opened" | "signed_in" | "extension_active";
  label: string;
  // Extra words under the label, where the tick means less than it says.
  note?: string;
  done: boolean;
  at: string | null;
};

/** The three checklist ticks from `prospect_progress`; missing or null means none is on. */
export const prospectTicks = (status: DemoPrepareStatus | null): ProspectTick[] => {
  const p = status?.prospect_progress;
  const opened = p?.link_opened_at ?? null;
  const seen = p?.extension_seen_at ?? null;
  return [
    { key: "signed_in", label: "Signed in to Dooglie", done: p?.signed_in === true, at: null },
    { key: "extension_active", label: "Extension active", done: !!seen, at: seen },
    {
      key: "link_opened",
      label: "CRM link opened",
      note: opened ? undefined : "(a test open counts too)",
      done: !!opened,
      at: opened,
    },
  ];
};

export const allTicksOn = (status: DemoPrepareStatus | null): boolean =>
  prospectTicks(status).every((t) => t.done);

/**
 * What a new status shows once merged over the last one. A poll of a ready
 * demo that comes back without `prospect_progress` (or with a tick missing)
 * keeps the ticks already seen: they never go backwards.
 */
export const mergeStatus = (
  prev: DemoPrepareStatus | null,
  next: DemoPrepareStatus,
): DemoPrepareStatus => {
  if (prev?.status !== "ready" || next.status !== "ready" || !prev.prospect_progress) return next;
  const a = prev.prospect_progress;
  const b = next.prospect_progress ?? {};
  return {
    ...next,
    prospect_progress: {
      signed_in: b.signed_in === true || a.signed_in === true,
      extension_seen_at: b.extension_seen_at ?? a.extension_seen_at ?? null,
      link_opened_at: b.link_opened_at ?? a.link_opened_at ?? null,
    },
  };
};

/** The 422 `salesperson_has_no_name` refusal, in the salesperson's words. */
export const NO_NAME_MESSAGE =
  "Set your first and last name in Settings before preparing a demo — the prospect's CRM will show you under that name.";

/** What a refused `prepare` means, in the salesperson's words. */
export const prepareRefusalMessage = (e: unknown): string => {
  const body = e as DemoPrepareRefusal | undefined;
  switch (body?.error) {
    case "prospect_has_org":
      return body.org_name
        ? `This prospect already has a Dooglie account, in ${body.org_name}.`
        : "This prospect already has a Dooglie account.";
    case "domain_has_org":
      return "The prospect's company already has a Dooglie org.";
    case "in_progress":
      return "A demo is already being prepared. Tear it down before starting another.";
    case "salesperson_has_no_name":
      return NO_NAME_MESSAGE;
    case "invalid_site":
      return "That doesn't look like a website address.";
    case "site_required":
      return "That guest uses a personal email address. Enter the company website to build the demo from.";
    default:
      return apiErrorMessage(e);
  }
};

/** What a refused `retry` means, in the salesperson's words. */
export const retryRefusalMessage = (e: unknown): string => {
  switch ((e as DemoPrepareRefusal | undefined)?.error) {
    case "in_progress":
      return "This demo is already running again. Wait for it, or tear it down.";
    case "teardown_failed":
      return "The teardown failed, so there is nothing to retry. Tear the demo down again.";
    case "not_retryable":
      return "There is nothing to retry: this demo has not failed.";
    default:
      return apiErrorMessage(e);
  }
};

/** What a refused `crm_link` means, in the salesperson's words. */
export const crmLinkRefusalMessage = (e: unknown): string => {
  switch ((e as DemoPrepareRefusal | undefined)?.error) {
    case "crm_link_unsupported":
      return "The CRM doesn't support sign-in links yet.";
    case "crm_link_not_ready":
      return "The CRM isn't ready yet — try again in a moment.";
    case "not_ready":
      return "The demo is not ready yet.";
    default:
      return apiErrorMessage(e);
  }
};

export const demo = createModel<RootModel>()({
  state: {
    status: null,
    busy: false,
    error: null,
    errorCode: null,
  } as DemoState,

  reducers: {
    setBusy(state) {
      return { ...state, busy: true, error: null, errorCode: null };
    },
    setError(state, error: string) {
      return { ...state, busy: false, error, errorCode: null };
    },
    setRefusal(state, refusal: { message: string; code: string | null }) {
      return { ...state, busy: false, error: refusal.message, errorCode: refusal.code };
    },
    clearError(state) {
      return { ...state, error: null, errorCode: null };
    },
    setStatus(state, status: DemoPrepareStatus) {
      return { ...state, status: mergeStatus(state.status, status) };
    },
    setDone(state) {
      return { ...state, busy: false, error: null, errorCode: null };
    },
  },

  effects: (dispatch) => ({
    // On app start (staff only) and on every poll while a preparation runs.
    async fetchStatus(): Promise<DemoPrepareStatus | null> {
      try {
        const status = await demoSession({ action: "prepare_status" });
        if (!status || typeof status.status !== "string") return null;
        dispatch.demo.setStatus(status);
        return status;
      } catch (error) {
        console.error("Failed to load the demo status:", error);
        return null;
      }
    },

    // Handed back to the caller rather than kept: the list belongs to the
    // opening of the dialog that asked for it.
    async fetchMeetings(): Promise<DemoMeetings> {
      return demoSession({ action: "meetings" });
    },

    // Each action records its refusal in `error` and rethrows, so the caller
    // knows not to move on. Prepare and teardown wait out a `demo_processing`
    // refusal rather than reporting it.
    async prepare(body: PrepareDemoBody) {
      dispatch.demo.setBusy();
      try {
        const { site, ...rest } = body;
        const trimmed = site?.trim();
        await untilProcessed(
          () =>
            demoSession({
              action: "prepare",
              ...rest,
              ...(trimmed ? { site: trimmed } : {}),
              brief_override: null,
            }),
          { onWait: () => undefined },
        );
        dispatch.demo.setStatus({ status: "requested", prospect_email: body.prospect_email });
        dispatch.demo.setDone();
      } catch (error) {
        const code = (error as DemoPrepareRefusal | undefined)?.error;
        dispatch.demo.setRefusal({
          message: prepareRefusalMessage(error),
          code: typeof code === "string" ? code : null,
        });
        throw error;
      }
    },

    // Runs a failed demo's failed step again. The server answers with the step
    // it restarted at; the status is set here at once, so the dialog sees a
    // preparing demo and resumes polling without waiting for its next fetch.
    async retry(_: void, rootState) {
      dispatch.demo.setBusy();
      try {
        const { status } = await untilProcessed(() => demoSession({ action: "retry" }), {
          onWait: () => undefined,
        });
        dispatch.demo.setStatus({ ...rootState.demo.status, status, error: null });
        dispatch.demo.setDone();
      } catch (error) {
        const code = (error as DemoPrepareRefusal | undefined)?.error;
        dispatch.demo.setRefusal({
          message: retryRefusalMessage(error),
          code: typeof code === "string" ? code : null,
        });
        // These two refusals mean the dialog's status is out of date (another
        // click, or the worker, moved the demo on): adopt the server's, so the
        // dialog shows the real step and resumes polling instead of sitting on
        // a stale `failed`.
        if (code === "in_progress" || code === "not_retryable") {
          await dispatch.demo.fetchStatus();
        }
        throw error;
      }
    },

    // The server answers 202 and works through the teardown; the dialog
    // keeps polling until `prepare_status` says `none`.
    async teardown() {
      dispatch.demo.setBusy();
      try {
        await untilProcessed(() => demoSession({ action: "teardown" }), {
          onWait: () => undefined,
        });
        dispatch.demo.setDone();
      } catch (error) {
        dispatch.demo.setError(apiErrorMessage(error));
        throw error;
      }
    },
  }),
});
